import React from "react";
import { useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { USE_CASES, LANGUAGES } from "@/data/content";
import { Reveal, StaggerGroup, StaggerChild } from "@/components/site/Reveal";
import { PageAtmosphere, FloatChip, EyebrowPill } from "@/components/site/PageAtmosphere";

export default function UseCases() {
    const { openDemo } = useOutletContext();

    return (
        <div className="bg-warm">
            {/* HERO */}
            <section className="relative overflow-hidden pt-20 pb-16 bg-warm">
                <PageAtmosphere />
                <FloatChip icon="fa-solid fa-stethoscope" label="Patient triage" accent="#A8B36E" delay={0.2} className="absolute top-24 left-[6%]" />
                <FloatChip icon="fa-solid fa-bag-shopping" label="Cart recovery" accent="#D4B542" delay={0.5} className="absolute top-40 right-[7%]" />
                <FloatChip icon="fa-solid fa-building-columns" label="EMI reminders" accent="#5FA88A" delay={0.8} className="absolute bottom-6 left-[14%]" />
                <div className="container-x relative z-10 max-w-4xl mx-auto text-center">
                    <Reveal className="mb-6">
                        <EyebrowPill label="Use cases" accent="#5FA88A" testid="use-cases-eyebrow" />
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h1 className="heading-hero text-5xl sm:text-6xl lg:text-7xl">
                            Deployed across
                            <br />
                            <span className="muted">every industry.</span>
                        </h1>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-8 text-intro text-slate-600 max-w-2xl mx-auto text-base md:text-lg">
                            From patient triage to EMI reminders, cart recovery to site-visit booking —
                            NyraAI runs proven playbooks across 8 industries in 22+ languages.
                        </p>
                    </Reveal>
                    <Reveal delay={0.3}>
                        <div className="mt-8">
                            <button onClick={openDemo} className="pill-dark" data-testid="use-cases-hero-cta">
                                Book a demo
                            </button>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* CASES GRID */}
            <section className="pb-16 bg-warm">
                <div className="container-x">
                    <StaggerGroup className="grid md:grid-cols-2 gap-6">
                        {USE_CASES.map((u) => (
                            <StaggerChild key={u.key}>
                                <motion.div
                                    id={u.key}
                                    whileHover={{ y: -3 }}
                                    transition={{ duration: 0.3 }}
                                    className="gleap-card p-8 h-full"
                                    data-testid={`usecase-${u.key}`}
                                >
                                    <div className="flex items-start gap-5">
                                        <div
                                            className="icon-sticker w-12 h-12 shrink-0"
                                            style={{ "--st-color": u.accent }}
                                        >
                                            <i className={`${u.icon} text-lg`} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <h2
                                                className="font-serif text-2xl text-slate-900"
                                            >
                                                {u.name}
                                            </h2>
                                            <p className="text-slate-500 text-sm mt-1">{u.subtitle}</p>
                                        </div>
                                    </div>

                                    <ul className="mt-6 space-y-2.5">
                                        {u.bullets.map((b) => (
                                            <li key={b} className="flex items-start gap-2.5">
                                                <i
                                                    className="fa-solid fa-check text-xs mt-1.5 shrink-0"
                                                    style={{ color: u.accent }}
                                                />
                                                <span className="text-slate-700 text-sm leading-relaxed">{b}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    <div className="mt-6 pt-6 border-t border-slate-200">
                                        <button
                                            onClick={openDemo}
                                            className="text-sm inline-flex items-center gap-2 text-slate-900 font-medium hover:gap-3 transition-all"
                                            data-testid={`usecase-cta-${u.key}`}
                                        >
                                            Talk to a {u.name} specialist
                                            <i className="fa-solid fa-arrow-right text-xs" />
                                        </button>
                                    </div>
                                </motion.div>
                            </StaggerChild>
                        ))}
                    </StaggerGroup>
                </div>
            </section>

            {/* LANGUAGES — mesh-framed */}
            <section className="py-20 bg-warm">
                <div className="container-x">
                    <Reveal>
                        <div className="mesh-frame text-center py-14">
                            <div className="text-sm text-slate-700 mb-3">Multilingual by design</div>
                            <h2
                                className="heading-section text-3xl md:text-5xl text-slate-900 mb-4"
                            >
                                NyraAI speaks your language.
                            </h2>
                            <p className="text-slate-700 max-w-xl mx-auto mb-8">
                                English plus 22+ major Indian languages — natively trained, not translated.
                            </p>
                            <div className="flex flex-wrap justify-center gap-2.5 max-w-2xl mx-auto">
                                {LANGUAGES.map((lang, i) => (
                                    <div
                                        key={i}
                                        className="glass-card px-4 py-1.5 text-slate-800 text-sm"
                                        style={{ borderRadius: 999 }}
                                        data-testid={`lang-${i}`}
                                    >
                                        {lang}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 md:py-32 bg-warm">
                <div className="container-x text-center max-w-3xl mx-auto">
                    <Reveal>
                        <h2 className="heading-hero text-4xl sm:text-5xl md:text-6xl">
                            Bring us your workflow.
                            <br />
                            <span className="muted">We&apos;ll show you the loop.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                            <button onClick={openDemo} className="pill-dark" data-testid="use-cases-footer-cta">
                                Book a demo
                            </button>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
